import { camelCase, kebabCase } from 'lodash';

interface Options {
  transformName?: (name: string) => string;
  transformValue?: <T>(name: string, value: T) => T | string;
}

/**
 * Maps each prop to its corresponding className while optionally applying name/value transformers
 */
const useGeneratedClasses = <P extends object, S extends Record<string, string>>(
  props: P,
  styles: S,
  options?: Options
) => {
  const { transformName, transformValue }: Options = {
    transformName: (name) => name,
    transformValue: (name, value) => value,
    ...options
  };

  /**
   * Takes the prop that can be a breakpoint configuration object or a primitive value
   * and returns the corresponding className(s)
   */
  const parseProp = <T>(name: string, value: T) => {
    const property = transformName(name);

    // handle object syntax - { base: 'column', md: 'row' }
    if (value && typeof value === 'object') {
      return Object.entries(value).map(([breakpoint, val]) => {
        const parts = [kebabCase(property), transformValue(property, val), breakpoint];

        if (breakpoint === 'base') {
          parts.pop();
        }

        return parts.join('-');
      });
    }

    return [`${kebabCase(property)}-${transformValue(property, value)}`];
  };

  const classes = Object.entries(props)
    .filter(([prop, value]) => value !== undefined)
    // merge all classes
    .reduce<string[]>((classes, [prop, value]) => classes.concat(parseProp(prop, value)), [])
    // map them to css modules
    .map((className) => styles[camelCase(className)]);

  return classes;
};

export default useGeneratedClasses;
